import { Observable } from 'rxjs';
import { map } from 'rxjs/operators';

import { RestService } from '../../services/rest/rest.service';
import { RestOptions } from '../../services/rest/rest-request';
import { Course, Lesson, Courses, Lessons, CourseLesson } from '../../domain/learn/learn.domain';
import { BrightcoveTranscriptService } from '../../features/brightcove-player/brightcove-transcript.service';

import { FilterCategory, LearnLike, LearnSort } from './learn.domain';

export class LearnRestBaseService {
  constructor(protected restService: RestService, protected brightcoveTranscriptService: BrightcoveTranscriptService) {
  }

  retrieveCourses(filter: Array<string>, sort?: LearnSort, offset?: number, limit?: number): Observable<Courses> {
    const parameters = {
      params: { filter, sort, offset, limit }
    } as RestOptions;
    return this.restService.getResource('education.courses').get<Courses>(parameters);
  }

  retrieveLessons(filter: Array<string>, sort?: LearnSort, offset?: number, limit?: number,
                  savedOnly?: boolean, showProgress?: boolean): Observable<Lessons> {
    const parameters = {
      params: { filter, sort, offset, limit, savedOnly, showProgress }
    } as RestOptions;

    // By default, do not send show progress to backend
    if (showProgress === undefined || showProgress === null) {
      delete parameters.params.showProgress;
    }

    return this.restService.getResource('education.lessons').get<Lessons>(parameters);
  }

  retrieveLessonsInCourse(courseId: string): Observable<Array<CourseLesson>> {
    const parameters = {
      pathVariables: { courseId }
    } as RestOptions;
    return this.restService.getResource('education.courseLessons').get<Array<CourseLesson>>(parameters)
      .pipe(
        map( (lessons: Array<CourseLesson>): Array<CourseLesson> => {
          if (lessons !== undefined && lessons.length > 0) {
            lessons.forEach((lesson: CourseLesson, index: number): void => {
              lesson.lessonNumber = index + 1;
              lesson.textTrack = this.parseTranscript(lesson.textTrack);
            });
          }
          return lessons;
        })
      );
  }

  retrieveCategories(): Observable<Array<FilterCategory>> {
    return this.restService.getResource('education.categories').get<Array<FilterCategory>>();
  }

  retrieveLesson(lessonKey: string): Observable<Lesson> {
    const restOptions: RestOptions = {
      pathVariables: { lessonKey }
    };
    return this.restService.getResource('education.lesson').get<Lesson>(restOptions)
      .pipe(
        map( (lesson: Lesson): Lesson => {
          if (lesson !== undefined) {
            lesson.textTrack = this.parseTranscript(lesson.textTrack);
          }
          return lesson;
        })
      );
  }

  retrieveCourseDetails(courseId: string): Observable<Course> {
    const restOptions: RestOptions = {
      pathVariables: { courseId }
    };
    return this.restService.getResource('education.courseById').get<Course>(restOptions);
  }

  postLike(like: LearnLike): Observable<number> {
    return this.restService.getResource('education.likeOrUnlike').post<LearnLike, number>(like);
  }

  mostLikedLessons(): Observable<Lessons> {
    return this.restService.getResource('education.mostLikedLessons').get<Lessons>();
  }

  recommendedLessons(filter: Array<string>): Observable<Array<Lesson>> {
    const parameters = {
      params: { filter }
    } as RestOptions;
    return this.restService.getResource('education.recommendations').get<Array<Lesson>>(parameters);
  }

  newAccountLessons(filter: Array<string>): Observable<Array<Lesson>> {
    const parameters = {
      params: { filter }
    } as RestOptions;
    return this.restService.getResource('education.newAccount').get<Array<Lesson>>(parameters);
  }

  selectedTopicLessons(): Observable<Array<Lesson>> {
    return this.restService.getResource('education.preferenceLessons').get<Array<Lesson>>();
  }

  addVideoBookmark(videoId: string): Observable<void> {
    const parameters = {
      pathVariables: { videoId }
    } as RestOptions;
    return this.restService.getResource('education.addVideoBookmark').post(undefined, parameters);
  }

  deleteVideoBookmark(videoId: string): Observable<void> {
    const parameters = {
      pathVariables: { videoId }
    } as RestOptions;
    return this.restService.getResource('education.deleteVideoBookmark').del<void>(undefined, parameters);
  }

  toggleVideoBookMark(lesson: Lesson): Observable<void> {
    return lesson.saved
      ? this.deleteVideoBookmark(lesson.contentMetaData.id)
      : this.addVideoBookmark(lesson.contentMetaData.id);
  }

  protected parseTranscript(textTrack: string): string {
    if (textTrack !== undefined) {
      return this.brightcoveTranscriptService.parse(textTrack);
    }
  }
}

